import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { sandhillsRequest, focusErroredField, gtmPushEvent, insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import SelectField from '@Components/Shared/Controls/SelectField';
import TextField from '@Components/Shared/Controls/TextField';
import RadioButtonList from '@Components/Shared/Controls/RadioButtonList';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import EmailAlertsListView from '@Components/WantToBuy/EmailAlertsListView';
import EmailAlertsChangesSavedModal from '@Components/Shared/Modals/EmailAlertsChangesSavedModal';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { UnformatCurrency } from '@Components/Utilities/CurrencyFormatter';
import { sortDistanceWithEnteredLocation, runSortWithCurrentLocation } from '@Components/Utilities/DistanceSortHelper';
import { listingAlertSpecNamesToCheck, topLevelClassificationsToSkipLowerClassifications, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { ThemedButton } from '@Components/MuiWrappers/Button';
import { useRecoilValue } from 'recoil';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { debounce } from 'lodash';

const WantToBuyView = (props) => {

    const siteHasClassifications = props.SelectListItems.TopClassificationOptions && props.SelectListItems.TopClassificationOptions.length > 0;
    const topClassificationIDSelected = props.TopClassificationID || (siteHasClassifications ? props.SelectListItems.TopClassificationOptions[0].Value : '');
    const alertTypeName = props.AlertTypeID === 1 ? translator.getPlainTextPhrase("sandhills.wanttobuys") : translator.getPlainTextPhrase("sandhills.emailalerts");
    const routes = useRecoilValue(atoms.routes);
    const requestUrl = props.AlertTypeID === 1 ? routes.WantToBuySave : routes.SaveEmailAlerts;
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);

    const getTopSelectedClassificationLabel = (classificationID) => {
        if (siteHasClassifications) {
            let selectedTopClassification = props.SelectListItems.TopClassificationOptions.filter((c) => { return c.Value == classificationID });
            if (selectedTopClassification && selectedTopClassification.length > 0) {
                return selectedTopClassification[0].Text;
            }
        }
        return '';
    };

    const [emailAlerts, setEmailAlerts] = useState(props.EmailAlerts);
    const windowSize = useWindowSize();
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedTopClassificationLabel, setSelectedTopClassificationLabel] = useState(getTopSelectedClassificationLabel(topClassificationIDSelected));
    const [showEquipmentInputDetails, setShowEquipmentInputDetails] = useState(props.WantToBuyMessageID != '0' || (props.SelectListItems.EquipmentTypeOptions && props.SelectListItems.EquipmentTypeOptions.length == 1));
    const [mileageUnit, setMileageUnit] = useState((props.SelectListItems.MileageUnitOptions && props.SelectListItems.MileageUnitOptions.length > 0) ? props.SelectListItems.MileageUnitOptions[0].Value : '');
    const [horsepowerUnit, setHorsepowerUnit] = useState((props.SelectListItems.HorsepowerUnitOptions && props.SelectListItems.HorsepowerUnitOptions.length > 0) ? props.SelectListItems.HorsepowerUnitOptions[0].Value : '');
    const [availableSpecs, setAvailableSpecs] = useState(props.AvailableSpecs);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [formData, setFormData] = useState({
        WantToBuyMessageID: props.WantToBuyMessageID,
        Model: props.Model,
        ModelIsRequired: props.ModelIsRequired,
        CategoryID: props.CategoryID || '',
        TypedManufacturer: props.TypedManufacturer || '',
        EquipmentType: props.EquipmentType,
        YearFrom: props.YearFrom,
        YearTo: props.YearTo,
        PriceLimit: props.FormattedPrice ? UnformatCurrency(props.FormattedPrice, props.CultureCode, props.CurrencyCode, false, true) : '',
        CurrencyCode: props.CurrencyCode,
        LanguageID: '',
        AlertTypeID: props.AlertTypeID,
        PlanningToBuy: props.PlanningToBuy,
        QuantityNeeded: props.QuantityNeeded || 1,
        FitsToCategoryID: props.FitsToCategoryID,
        ShortDescription: props.ShortDescription || '',
        OtherComments: props.OtherComments || '',
        SerialNumber: props.SerialNumber || '',
        PartNumber: props.PartNumber || '',
        CultureCode: props.CultureCode,
        MileageMin: props.MileageMin,
        MileageMax: props.MileageMax,
        HoursMin: props.HoursMin,
        HoursMax: props.HoursMax,
        HorsepowerMin: props.HorsepowerMin,
        HorsepowerMax: props.HorsepowerMax,
        LengthInFeet: props.LengthInFeet,
        LengthInInches: props.LengthInInches,
        WidthInInches: props.WidthInInches,
        TopClassificationID: topClassificationIDSelected,
        TopClassificationCategoryID: props.TopClassificationCategoryID,
        showAlertNumberExceededMessage: props.AlertNumberExceeded,
        UserEnteredLocation: props.UserEnteredLocation,
        MaxDistance: props.MaxDistance,
        Engine: props.Engine,
        AddToWatchlist: true,
        CategoryDisplayName: props.CategoryDisplayName,
        IsFTIWTB: props.IsFTIWTB || props.InitWholesale || false
    });
    const [userEnteredLocation, setUserEnteredLocation] = useState();
    const [locationErrorMessage, setLocationErrorMessage] = useState();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const UseCurrentLocation = 'UseCurrentLocation';
    useEffect(() => {
        if (formData.showAlertNumberExceededMessage && (showEquipmentInputDetails || formData.AlertTypeID === 2 && emailAlerts.length === props.EmailAlerts.length) && !formData.WantToBuyMessageID > 0) {
            setIsSaveDisabled(true);
        }
    });

    const handleCancelClick = (e) => {
        e.preventDefault();
        if (formData.AlertTypeID === 1) {
            history.back();
        } else {
            clearForm();
        }
    };

    const inputReferences = useRef({});
    const clearForm = (isFTI) => {
        setAvailableSpecs([]);
        setValidationErrors({});
        setLocationErrorMessage('');
        setFormData(previousState => {
            return {
                ...previousState,
                WantToBuyMessageID: 0,
                Model: '',
                CategoryID: '',
                TypedManufacturer: '',
                YearFrom: '',
                YearTo: '',
                PriceLimit: '',
                CurrencyCode: props.CurrencyCode,
                LanguageID: 'English',
                FitsToCategoryID: '',
                CultureCode: props.CultureCode,
                MileageMin: '',
                MileageMax: '',
                HorsepowerMin: '',
                HorsepowerMax: '',
                HoursMin: '',
                HoursMax: '',
                LengthInInches: '',
                LengthInFeet: '',
                WidthInInches: '',
                UserEnteredLocation: '',
                MaxDistance: '',
                Engine: '',
                TopClassificationCategoryID: '',
                ModelIsRequired: props.ModelIsRequired,
                AddToWatchlist: true,
                IsFTIWTB: isFTI || false
            };
        });
        setUserEnteredLocation('');
    };

    const checkForSpecs = useCallback((catID, successCallback, errorCallback) => {
        if (catID) {
            let data = { 'categoryId': catID, 'specNames': listingAlertSpecNamesToCheck.validSpecNames, 'includeTrailerSpecs': formData.AlertTypeID === 1, "categoryContext": props.CategoryContextOverride };
            sandhillsRequest({
                url: routes.CategorySpecCheck,
                method: 'POST',
                data: data,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then(successCallback).catch(errorCallback);
        }
    }, [formData.AlertTypeID, routes.CategorySpecCheck, props.CategoryContextOverride]);

    /*
     * Grabs the categories based on manu and model inputs,
     * otherwise sets categories to an empty array which triggers
     * the category dropdown to grab all the categories.
     */
    const getCategories = (manu, model) => {
        if (manu && (model || !formData.ModelIsRequired)) {
            sandhillsRequest({
                url: routes.GetWantToBuyDrilldown,
                data: {
                    manufacturer: manu,
                    model: model
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                let data = response || [];
                data.push({ Disabled: false, Group: null, Selected: false, Text: 'Show All Categories', Value: 'All' });

                if (data.filter(cat => !cat.IsParent && cat.Name).length == 1) {
                    const manualEvent = {
                        target: {
                            name: "CategoryID",
                            value: data.filter(cat => !cat.IsParent && cat.Name)[0].Value
                        }
                    }
                    handleCategoryDropdownFieldChange(manualEvent);
                }

                setCategories(data);
            });
        }
        else {

            setCategories([]);
        }
    };

    const getCategoriesRef = useRef(getCategories);
    getCategoriesRef.current = getCategories;

    const debouncedGetCategories = useMemo(() => debounce((manu, model) => getCategoriesRef.current(manu, model), 500), []);

    useEffect(() => {
        return () => debouncedGetCategories.cancel();
    }, [debouncedGetCategories]);

    const handleCategoryDropdownFieldChange = (e) => {
        /*
         * Necessary logic for ensuring data calls are made correctly and the form data is updated,
         * otherwise the user may be able to submit the form with an unselected category.
         */
        let value = e.target.value == 'All' ? '' : e.target.value;
        if (value == '' && e.target.selectedOptions[0].label == 'Show All Categories') {
            getCategories();
        }
        let modelRequired = formData.ModelIsRequired;
        if (props.ModelNotRequiredCats && props.ModelNotRequiredCats.length > 0) {
            let selectedCats = props.ModelNotRequiredCats.filter(cat => cat === value || value === '');
            modelRequired = selectedCats.length < 1;
        }
        checkForSpecs(value == '' ? 0 : value, (validSpecs) => {
            if (validSpecs && validSpecs.length > 0) {
                setAvailableSpecs(validSpecs);

            } else {
                setAvailableSpecs([]);
                setFormData(previousState => {
                    return { ...previousState, HorsepowerMin: '', HorsepowerMax: '', HoursMin: '', HoursMax: '', MileageMin: '', MileageMax: '' };
                });
            }
        }, () => {
            setAvailableSpecs([])
        });

        setFormData(previousState => {
            if (previousState.EquipmentType == '4') {
                return {
                    ...previousState,
                    ModelIsRequired: modelRequired,
                    CategoryID: value
                };
            } else {
                return {
                    ...previousState,
                    ModelIsRequired: modelRequired,
                    FitsToCategoryID: value
                };
            }
        });

    };

    const handleCurrencyChange = (e) => {
        let unformattedPrice = '';
        let newCurrencyCode = e.target.options[e.target.selectedIndex].text;
        let newCultureCode = e.target.value.substring(4);

        unformattedPrice = UnformatCurrency(formData.PriceLimit, newCultureCode, newCurrencyCode);

        setFormData(previousState => {
            return {
                ...previousState,
                CurrencyCode: newCurrencyCode,
                CultureCode: newCultureCode
            };
        });

        if (formData.PriceLimit) {
            setFormData(previousState => {
                return {
                    ...previousState,
                    PriceLimit: UnformatCurrency(unformattedPrice, newCultureCode, newCurrencyCode)
                };
            });
        }
    };

    const runValidation = () => {
        let pendingPriceLimit = UnformatCurrency(formData.PriceLimit, formData.CultureCode, formData.CurrencyCode);
        let rules = [
            ruleRunner('QuantityNeeded', <TranslationText tKey='sandhills.quantity' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1)),
            ruleRunner('QuantityNeeded', <TranslationText tKey='sandhills.quantity' />, ValidationRules.requiredIf(formData.AlertTypeID === 1)),
            ruleRunner('LanguageID', <TranslationText tKey='Language' />, ValidationRules.requiredIf(formData.AlertTypeID === '2')),
            ruleRunner('PlanningToBuy', <TranslationText tKey='sandhills.planningtobuy' />, ValidationRules.requiredIf(formData.AlertTypeID === 1)),
            ruleRunner('mileageUnit', 'Max Distance (Miles):', ValidationRules.requiredIf(userEnteredLocation && !formData.MaxDistance)),
            ruleRunner('mileageUnit', 'Max Distance (Miles):', ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)),
            ruleRunner('UserEnteredLocation', <TranslationText tKey='sandhills.citystatezip' />, ValidationRules.requiredIf(formData.MaxDistance > 0 && !userEnteredLocation))
        ];

        if (siteHasClassifications || formData.TopClassificationID) {
            if (formData.TopClassificationCategoryID && !topLevelClassificationsToSkipLowerClassifications.includes(Number(formData.TopClassificationCategoryID))) {
                rules.push(ruleRunner('CategoryID', props.SelectListItems.LowerClassificationLabelMappings[formData.TopClassificationCategoryID], ValidationRules.required));
            }
            rules.push(ruleRunner('TopClassificationCategoryID', selectedTopClassificationLabel, ValidationRules.required));
        } else {
            if (formData.ModelIsRequired) {
                rules.push(ruleRunner('Model', <TranslationText tKey='sandhills.Equipmentmodel' />, ValidationRules.required, ValidationRules.minLength(2)));
            }

            rules.push(ruleRunner('TypedManufacturer', <TranslationText tKey='sandhills.equipmentmanufacturer' />, ValidationRules.required, ValidationRules.minLength(2)));
            rules.push(ruleRunner('EquipmentType', <TranslationText tKey='sandhills.EquipmentType' />, ValidationRules.requiredIf(formData.AlertTypeID === 1)));
            rules.push(ruleRunner('FitsToCategoryID', <TranslationText tKey='sandhills.categoryisneededfor' replaceValues={formData.EquipmentType == '3' ? [<TranslationText tKey='sandhills.CRM.DSCRM.Attachment' />] : [<TranslationText tKey='sandhills.parts' />]} />, ValidationRules.requiredIf(formData.EquipmentType == '3' || formData.EquipmentType == '9')));
            rules.push(ruleRunner('YearFrom', <TranslationText tKey='sandhills.yearfrom' />, ValidationRules.mustBeLessThanOrEqualTo('YearTo', <TranslationText tKey='sandhills.yearto' />)));
            rules.push(ruleRunner('MileageMax', <TranslationText tKey='sandhills.mileageto' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1)));
            rules.push(ruleRunner('MileageMin', <TranslationText tKey='sandhills.mileagefrom' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('MileageMin', <TranslationText tKey='sandhills.mileagefrom' />, ValidationRules.mustBeLessThanOrEqualTo('MileageMax', <TranslationText tKey='sandhills.mileageto' />)));
            rules.push(ruleRunner('HoursMax', <TranslationText tKey='sandhills.hoursto' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1)));
            rules.push(ruleRunner('HoursMin', <TranslationText tKey='sandhills.hoursfrom' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('HoursMin', <TranslationText tKey='sandhills.hoursfrom' />, ValidationRules.mustBeLessThanOrEqualTo('HoursMax', <TranslationText tKey='sandhills.hoursto' />)));
            rules.push(ruleRunner('HorsepowerMax', <TranslationText tKey='sandhills.horsepowerto' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1)));
            rules.push(ruleRunner('HorsepowerMin', <TranslationText tKey='sandhills.horsepowerfrom' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('HorsepowerMin', <TranslationText tKey='sandhills.horsepowerfrom' />, ValidationRules.mustBeLessThanOrEqualTo('HorsepowerMax', <TranslationText tKey='sandhills.horsepowerto' />)));
            rules.push(ruleRunner('PriceLimit', <TranslationText tKey='sandhills.pricelimit' />, ValidationRules.isNumeric));
            rules.push(ruleRunner('LengthInFeet', <TranslationText tKey='sandhills.Length' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('LengthInInches', <TranslationText tKey='sandhills.Length' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('WidthInInches', <TranslationText tKey='sandhills.width' />, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(0)));
            rules.push(ruleRunner('CategoryID', <TranslationText tKey='sandhills.category' />, ValidationRules.requiredIf(formData.EquipmentType == '3' || formData.EquipmentType == '4')));
        }

        return run({
            ...formData,
            PriceLimit: pendingPriceLimit
        }, rules);
    }

    const submitForm = (e, forceEAReload) => {
        e?.preventDefault();
        if (!formData.showAlertNumberExceededMessage || formData.WantToBuyMessageID !== 0) {
            let pendingPriceLimit = UnformatCurrency(formData.PriceLimit, formData.CultureCode, formData.CurrencyCode);

            let categoryID = formData.CategoryID;

            let errors = runValidation();

            setValidationErrors(errors);
            focusErroredField(errors, inputReferences);

            if (pendingPriceLimit) {
                if (isNaN(pendingPriceLimit)) {
                    if (UnformatCurrency(0, formData.CultureCode, formData.CurrencyCode) === pendingPriceLimit) {
                        pendingPriceLimit = '';
                    } else {
                        pendingPriceLimit = UnformatCurrency(pendingPriceLimit, formData.CultureCode, formData.CurrencyCode);
                    }
                }
            }

            if (Object.keys(errors).length <= 0) {
                setIsSaveDisabled(true);
                sandhillsRequest({
                    url: requestUrl,
                    method: 'post',
                    data: {
                        ...formData,
                        PriceLimit: pendingPriceLimit,
                        CategoryID: categoryID,
                        UserEnteredLocation: userEnteredLocation
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (props.AlertTypeID === 2 && data.Success) {
                        setShowSuccessModal(true);
                        gtmPushEvent(gtmEventType.newListingEmail);
                        setTimeout(() => {
                            setShowSuccessModal(false);
                            if (forceEAReload) {
                                window.location.href = props.cleanLoadURL;
                            }
                        }, 2000);
                    }
                    if (data.URL) {
                        if (data.AlertNumberExceeded && !data.WantToBuyMessageID > 0) {
                            setFormData(previousState => {
                                return {
                                    ...previousState,
                                    showAlertNumberExceededMessage: true
                                };
                            });
                        }
                        else {
                            setIsSaveDisabled(false);
                            window.location.href = data.URL;
                        }
                    } else if (data?.Success == false) {
                        setLocationErrorMessage(data.Message);
                        setIsSaveDisabled(false);
                        inputReferences?.current[`LocationField`]?.focus?.();
                    } else if (props.cleanLoadURL) {
                        setIsSaveDisabled(false);
                        if (props.AlertTypeID === 1) {
                            window.location.href = props.cleanLoadURL;
                        }
                    } else {
                        setIsSaveDisabled(false);
                        window.location.href = data;
                    }
                });
            }
        }
    };

    /*
     * Autocomplete handling functions added to be more consistent with EA logic
     * and allows for more control ovwr the updateField property of the TextField component
     */ 
    const handleManuAutocomplete = (value) => {
        setFormData(previousState => {
            return { ...previousState, TypedManufacturer: value };
        })
        fieldChange('TypedManufacturer', value);
    };

    const handleModelAutocomplete = (value) => {
        setFormData(previousState => {
            return { ...previousState, Model: value };
        })
        fieldChange('Model', value);
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteManuData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: props.EquipmentDisplayCategoryIDs.join(','),
        };
    };

    const getAutoCompleteModelData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: props.EquipmentDisplayCategoryIDs.join(','),
            manufacturer: formData.TypedManufacturer
        };
    };

    const onFieldBlur = (e, fieldName) => {
        if (fieldName === 'TypedManufacturer' && (formData.Model == '' || formData.Model == null)) {
            debouncedGetCategories(e.target.value, formData.Model);
        }
        else if (fieldName === 'Model') {
            debouncedGetCategories(formData.TypedManufacturer, e.target.value);
        }
    };

    const getAutoCompleteCallback = () => {
        return (e, ui) => { setUserEnteredLocation(ui.item.value); }
    };

    const getAutoCompleteLocationUrl = (e) => {
        return routes.LocationAutosuggestUrl + encodeURI(e.target.value);
    };

    const onKeyPress = (e) => {
        if (e.code === 'Enter') {
            handleLocationFieldChange(e);
        }
    };

    const onLocationFieldBlur = (e) => {
        if (props.CurrentUserFormattedAddress != userEnteredLocation) {
            if (!userEnteredLocation) {
                setUserEnteredLocation(props.CurrentUserFormattedAddress);
            }
            else {
                handleLocationFieldChange(e);
            }
        }
    };

    const handleLocationFieldChange = (e) => {
        setUserEnteredLocation(e.target.value);
    };
    const enterUsersCurrentLocation = (e) => {
        e.preventDefault();
        runSortWithCurrentLocation(
            insertURLParameter(routes.FormattedLocationStringFromCurrentCoordinates, UseCurrentLocation, true),
            routes.AjaxGetCoordinatesFromLocationStringUrl,
            props.GEOLocationPositionModel.CurrentAuthenticatedUserLocationForCoordinates,
            routes.AjaxSetCurrentUserLocationCookie,
            routes.AjaxGetDecryptedCoordinatesFromCurrentLocationCookie
        ).then(location => {
            setUserEnteredLocation(location.formattedAddress)
        }).catch(() => {
            setLocationErrorMessage('Error obtaining your location. Please try again.');
        });
    };

    const deleteEmailAlert = (id) => {
        sandhillsRequest({
            url: routes.DeleteEmailAlerts.replace("{0}", id),
            method: 'post',
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setEmailAlerts(emailAlerts.filter(emailAlert => emailAlert.WantToBuyMessageID != id));
            setIsSaveDisabled(false);
            setFormData(previousState => {
                return {
                    ...previousState,
                    showAlertNumberExceededMessage: data.alertNumberExceeded
                }
            });
        });
    }
    const closeSuccessModal = () => {
        setShowSuccessModal(false);
    }

    const handleAddClick = () => {
        clearForm();
        inputReferences?.current[`TypedManufacturer`]?.focus?.();
    };

    const textChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value == 'All' ? '' : value };
        });

    };
    const fieldChange = (name, value) => {
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    }

    const titleSort = (type, isAsc) => {
        let copy = [...emailAlerts];
        switch (type) {
            case 'category':
                if (isAsc) {
                    setEmailAlerts(copy.sort((a, b) => (a.CategoryDisplayName > b.CategoryDisplayName) ? 1 : -1));
                } else {
                    setEmailAlerts(copy.sort((a, b) => (a.CategoryDisplayName < b.CategoryDisplayName) ? 1 : -1));
                }
                break;
            case 'manufacturer':
                if (isAsc) {
                    setEmailAlerts(copy.sort((a, b) => (a.TypedManufacturer > b.TypedManufacturer) ? 1 : -1));
                } else {
                    setEmailAlerts(copy.sort((a, b) => (a.TypedManufacturer < b.TypedManufacturer) ? 1 : -1));
                }
                break;
            case 'model':
                if (isAsc) {
                    setEmailAlerts(copy.sort((a, b) => (a.Model > b.Model) ? 1 : -1));
                } else {
                    setEmailAlerts(copy.sort((a, b) => (a.Model < b.Model) ? 1 : -1));
                }
                break;
            default:
                break;
        }
        return emailAlerts;
    }

    const toggleWatchlist = (e, addToWatchlist) => {
        setEmailAlerts(emailAlerts.filter(ea => ea.WantToBuyMessageID !== e.id));
        let modelRequired = formData.ModelIsRequired;
        if (props.ModelNotRequiredCats && props.ModelNotRequiredCats.length > 0) {
            let selectedCats = props.ModelNotRequiredCats.filter(cat => cat === e.CategoryID || e.CategoryID === '');
            modelRequired = selectedCats.length < 1;
        }
        let data = {
            ...e,
            CultureCode: props.SelectListItems.CurrencyCodeOptions.filter(currencyCode => currencyCode.Text == e.CurrencyCode),
            ModelIsRequired: modelRequired,
            AddToWatchlist: addToWatchlist
        };
        sandhillsRequest({
            url: requestUrl,
            method: 'post',
            data: {
                ...data
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    }
    const toggleTextAlert = (e, enabledTextAlert) => {
        setEmailAlerts(emailAlerts.filter(ea => ea.WantToBuyMessageID !== e.WantToBuyMessageID));
        let modelRequired = formData.ModelIsRequired;
        if (props.ModelNotRequiredCats && props.ModelNotRequiredCats.length > 0) {
            let selectedCats = props.ModelNotRequiredCats.filter(cat => cat === e.CategoryID || e.CategoryID === '');
            modelRequired = selectedCats.length < 1;
        }
        let data = {
            ...e,
            CultureCode: props.SelectListItems.CurrencyCodeOptions.filter(currencyCode => currencyCode.Text == e.CurrencyCode),
            ModelIsRequired: modelRequired,
            TextAlertsEnabled: enabledTextAlert
        };
        sandhillsRequest({
            url: requestUrl,
            method: 'post',
            data: {
                ...data
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    }
    const toggleFTI = (e) => {
        let value = e.target.value;
        if (value == 'FTI') {
            setFormData(previousState => {
                return { ...previousState, EquipmentType: 4, IsFTIWTB: true };
            });
        } else {
            setFormData(previousState => {
                return { ...previousState, EquipmentType: 4, IsFTIWTB: false };
            });
        }
    }

    const header = (alertTypeId) => {
        switch (alertTypeId) {
            case 1:
                return (
                    <div>
                        {formData.showAlertNumberExceededMessage && !formData.WantToBuyMessageID > 0 && formData.AlertTypeID === 1 ?
                            <div className="alert-error-message">
                                <br />
                                <div className="error">
                                    {translator.translate("sandhills.tradesites.mvc.alertnumberexceeded", [alertTypeName, alertTypeName, alertTypeName])}&nbsp;
                                    {props.AlertTypeID === 1 && <a href="/my-want-to-buys" style={{ color: "black" }}><strong><TranslationText tKey="sandhills.viewmywtbs" /></strong></a>}
                                </div>
                            </div>
                            :
                            <div>
                                <h1 className='pagename border-bottom'><TranslationText tKey='sandhills.tradepubwebsite.postawanttobuy' /></h1>
                                <p><TranslationText tKey='sandhills.yourwanttobuywillbelistedonimmediately' replaceValues={[props.SiteDisplayName]} /></p>
                            </div>
                        }
                    </div>
                );
            case 2:
                return (
                    <div className='email-alert-title'>
                        <h3 className='pagename border-bottom uppercase mobile-hide'><TranslationText tKey='sandhills.tradesites.mvc.alertsfornewlistings' /></h3>
                        <a className='account-button title' href={routes.WatchList}><TranslationText tKey='sandhills.tradesites.mvc.viewmywatchlist' /></a>
                    </div>
                );
            default:
                return '';
        }
    };

    const formHeader = (alertTypeId) => {
        switch (alertTypeId) {
            case 1:
                return (
                    <div>
                        {props.WantToBuyMessageID == '0' ? <span className='wtb-header'><TranslationText tKey='sandhills.pleaseselectwtbtype' /></span> : ''}
                        <p><TranslationText tKey='sandhills.fieldsmarkedwith' replaceValues={['<span className="required red">*</span>']} /></p>
                        {currentUser.CanViewFTI &&
                            <div className='fti-toggle'>
                                <RadioButtonList
                                    options={[{ Text: translator.translate('sandhills.tradepubwebsite.mvc.retail'), Value: 'Retail', Checked: !formData.IsFTIWTB }, { Text: translator.translate('sandhills.crm.dscrm.wholesale'), Value: 'FTI', Checked: formData.IsFTIWTB }]}
                                    class='wtb-input-inline-radios'
                                    name='FTIType'
                                    value={formData.IsFTIWTB ? 'FTI' : 'Retail'}
                                    validationErrors={validationErrors.EquipmentType}
                                    onFieldChanged={(e) => {
                                        toggleFTI(e);
                                        setValidationErrors({});
                                    }}
                                />
                            </div>
                            }
                        {props.WantToBuyMessageID == '0' && props.SelectListItems.EquipmentTypeOptions && props.SelectListItems.EquipmentTypeOptions.length > 0 && !siteHasClassifications &&
                            <div className='capitalize'>
                                <fieldset className='ts__fieldset'>
                                    <legend className='ts__legend visually-hidden'><strong>{translator.translate('sandhills.crm.dscrm.lookingfor')}</strong></legend>
                                    <RadioButtonList
                                        options={formData.IsFTIWTB ? props.SelectListItems.EquipmentTypeOptions.filter(x => x.Value != '9') : props.SelectListItems.EquipmentTypeOptions}
                                        class='wtb-input-inline-radios'
                                        name='EquipmentType'
                                        value={formData.EquipmentType}
                                        validationErrors={validationErrors.EquipmentType}
                                        label={<TranslationText tKey='sandhills.crm.dscrm.lookingfor' />}
                                        required={true}
                                        onFieldChanged={(e) => {
                                            textChange(e);
                                            setValidationErrors({});
                                        }}
                                    />
                                </fieldset>
                            </div>}
                    </div>
                );
            case 2:
                return (
                    <>
                        <span className='wtb-header email-alert-header capitalize'><TranslationText tKey='Sandhills.TradePubWebsite.MVC.CantFindWhatYoureLookingFor' /></span>
                        {
                            formData.showAlertNumberExceededMessage &&
                            <div className="alert-error-message" >
                                <br />
                                <div className="error">
                                    {translator.translate("sandhills.tradesites.mvc.alertnumberexceeded", [alertTypeName, alertTypeName, alertTypeName])}&nbsp;
                                </div>
                            </div >}
                    </>
                );
            default:
                return (<></>);
        }
    };

    const wtbFields = () => {
        return (
            <div className='wtb-specific-fields'>
                {!siteHasClassifications &&
                    <TextField
                        name='QuantityNeeded'
                        value={formData.QuantityNeeded}
                        type='text'
                        label={<TranslationText tKey='sandhills.quantity' />}
                        class='wtb-input-half '
                        required={true}
                        validationErrors={validationErrors.QuantityNeeded}
                        ref={(element) => inputReferences.current[`QuantityNeeded`] = element}
                        onFieldChanged={textChange}
                    />}
                <TextField
                    name='ShortDescription'
                    value={formData.ShortDescription}
                    type='text'
                    label={<TranslationText tKey='sandhills.shortdesc' />}
                    class='wtb-input-full'
                    required={false}
                    validationErrors={validationErrors.ShortDescription}
                    ref={(element) => inputReferences.current[`ShortDescription`] = element}
                    onFieldChanged={textChange}
                />
                <div className='planning-to-buy-input'>
                    <fieldset className='ts__fieldset'>
                        <legend className='ts__legend visually-hidden'><strong>{translator.translate('sandhills.planningtobuy')}</strong></legend>
                        <RadioButtonList
                            options={props.SelectListItems.PlanningToBuyOptions}
                            class='planning-to-buy-option'
                            name='PlanningToBuy'
                            value={formData.PlanningToBuy}
                            validationErrors={validationErrors.PlanningToBuy}
                            label={<TranslationText tKey='sandhills.planningtobuy' />}
                            required={true}
                            onFieldChanged={textChange}
                        />
                    </fieldset>
                </div>
                <div className='wtb-input-full'>
                    <TextField
                        name='OtherComments'
                        value={formData.OtherComments}
                        type='textarea'
                        label={<TranslationText tKey='sandhills.othercomments' />}
                        class='wtb-input-other-comments'
                        required={false}
                        validationErrors={validationErrors.OtherComments}
                        ref={(element) => inputReferences.current[`OtherComments`] = element}
                        onFieldChanged={textChange}
                    />
                </div>
            </div>
        );
    };

    const listingAlertFields = () => {
        return (
            <>
                {(!siteHasClassifications || !formData.TopClassificationCategoryID || topLevelClassificationsToSkipLowerClassifications.includes(Number(formData.TopClassificationCategoryID))) &&
                    <div className='wtb-input-half '>
                        {props.SelectListItems.LanguageOptions && props.SelectListItems.LanguageOptions.length > 1 &&
                            <SelectField
                                name='LanguageID'
                                value={formData.LanguageID}
                                options={props.SelectListItems.LanguageOptions}
                                label={<TranslationText tKey='Sandhills.TradePubWebsite.MVC.EmailLanguage' />}
                                class='wtb-language'
                                required={false}
                                validationErrors={validationErrors.LanguageID}
                                ref={(element) => inputReferences.current[`LanguageID`] = element}
                                onFieldChanged={textChange}
                            />}
                    </div>
                }
            </>
        );
    };

    const equipmentBasedFields = () => {
        return (
            <>
                {formData.EquipmentType == '3' &&
                    <div className='wtb-input-half'>
                        {props.SelectListItems.AttachmentCategoryOptions && props.SelectListItems.AttachmentCategoryOptions.length > 0 &&
                            <SelectField
                                id='CategoryID'
                                name='CategoryID'
                                value={formData.CategoryID}
                                options={props.SelectListItems.AttachmentCategoryOptions}
                                label={<TranslationText tKey='sandhills.category' />}
                                class='wtb-input-full'
                                required={true}
                                validationErrors={validationErrors.CategoryID}
                                ref={(element) => inputReferences.current[`CategoryID`] = element}
                                onFieldChanged={textChange}
                            />}
                    </div>}
                {formData.EquipmentType == '3' &&
                    <span className='wtb-subtitle m-top-20'><TranslationText tKey='sandhills.EnterMachineInfoForAttachment' />.</span>}
                {formData.EquipmentType == '9' &&
                    <span className='wtb-subtitle m-top-20'><TranslationText tKey="sandhills.EnterMachineInfoForPart" />.</span>}
                <span className='wtb-manu-input'>
                    <fieldset className='ts__fieldset'>
                        <legend className='ts__legend visually-hidden'><strong>Either type in a manufacturer or select one from the list</strong></legend>
                        <TextField
                            name='TypedManufacturer'
                            id='TypedManufacturer'
                            value={formData.TypedManufacturer}
                            type='text'
                            label={<TranslationText tKey='sandhills.manufacturer' />}
                            class='wtb-input-half'
                            required={true}
                            validationErrors={validationErrors.TypedManufacturer}
                            onFieldChanged={textChange}
                            onFieldBlur={(e) => onFieldBlur(e, "TypedManufacturer")}
                            ref={(element) => inputReferences.current[`TypedManufacturer`] = element}
                            autoComplete={{
                                ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetManuAutoComplete),
                                postData: (e) => getAutoCompleteManuData(e)
                            }}
                            autoSuggestWidth={props.AlertTypeID === 1 ? -1 : null}
                            updateField={handleManuAutocomplete}
                            isWTBInput={true}
                        />
                    </fieldset>
                </span>
                <TextField
                    name='Model'
                    id='Model'
                    value={formData.Model}
                    type='text'
                    label={<TranslationText tKey='sandhills.model' />}
                    class='wtb-input-half'
                    required={formData.ModelIsRequired}
                    validationErrors={validationErrors.Model}
                    ref={(element) => inputReferences.current[`Model`] = element}
                    onFieldChanged={textChange}
                    onFieldBlur={(e) => onFieldBlur(e, "Model")}
                    autoComplete={{
                        ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetModelAutoComplete),
                        postData: (e) => getAutoCompleteModelData(e)
                    }}
                    autoSuggestWidth={props.AlertTypeID === 1 ? -1 : null}
                    updateField={handleModelAutocomplete}
                    isWTBInput={true}
                />
                {props.SelectListItems.CategoryOptions && props.SelectListItems.CategoryOptions.length > 0 &&
                    <SelectField
                        id={translator.translate('sandhills.category')}
                        name={formData.EquipmentType == '4' ? 'CategoryID' : 'FitsToCategoryID'}
                        value={formData.EquipmentType == '4' ? formData.CategoryID : formData.FitsToCategoryID}
                        options={categories.length > 0 ? categories : props.SelectListItems.CategoryOptions}
                        label={<TranslationText tKey='sandhills.category' />}
                        class='wtb-input-half '
                        required={true}
                        validationErrors={formData.EquipmentType == '4' ? validationErrors.CategoryID : validationErrors.FitsToCategoryID}
                        ref={(element) => { formData.EquipmentType == '4' ? inputReferences.current[`CategoryID`] = element : inputReferences.current['FitsToCategoryID'] = element }}
                        onFieldChanged={handleCategoryDropdownFieldChange}
                    />}
                <span>
                    {props.SelectListItems.YearOptions && props.SelectListItems.YearOptions.length > 0 &&
                        <SelectField
                            id={translator.translate('sandhills.year')}
                            name='YearFrom'
                            value={formData.YearFrom}
                            options={props.SelectListItems.YearOptions}
                            label={<TranslationText tKey={formData.EquipmentType == '4' ? 'sandhills.yearfrom' : 'sandhills.year'} />}
                            class='wtb-input-half'
                            required={false}
                            validationErrors={validationErrors.YearFrom}
                            onFieldChanged={textChange}
                        />}
                    {formData.EquipmentType == '4' && props.SelectListItems.YearOptions && props.SelectListItems.YearOptions.length > 0 &&
                        <SelectField
                            id={translator.translate('sandhills.yearto')}
                            name='YearTo'
                            value={formData.YearTo}
                            options={props.SelectListItems.YearOptions}
                            label={<TranslationText tKey='sandhills.yearto' />}
                            class='wtb-input-half '
                            required={false}
                            validationErrors={validationErrors.YearTo}
                            onFieldChanged={textChange}
                        />}
                </span>
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.hours.includes(o)) && formData.EquipmentType == '4' &&
                    <span>
                        <div className='wtb-input-half'>
                            <TextField
                                name='HoursMin'
                                value={formData.HoursMin}
                                type='text'
                                label={<TranslationText tKey='sandhills.hoursfrom' />}
                                required={false}
                                validationErrors={validationErrors.HoursMin}
                                ref={(element) => inputReferences.current[`HoursMin`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                        <div className='wtb-input-half '>
                            <TextField
                                name='HoursMax'
                                value={formData.HoursMax}
                                type='text'
                                label={<TranslationText tKey='sandhills.hoursto' />}
                                required={false}
                                validationErrors={validationErrors.HoursMax}
                                ref={(element) => inputReferences.current[`HoursMax`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                    </span>}
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.mileage.includes(o)) && formData.EquipmentType == '4' &&
                    <span>
                        <div className='wtb-input-half'>
                            <TextField
                                name='MileageMin'
                                value={formData.MileageMin}
                                type='text'
                                label={<TranslationText tKey='sandhills.mileagefrom' />}
                                required={false}
                                validationErrors={validationErrors.MileageMin}
                                ref={(element) => inputReferences.current[`MileageMin`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                        <div className='wtb-input-half '>
                            <TextField
                                name='MileageMax'
                                htmlFor={<TranslationText tKey='sandhills.tradepubwebsite.mvc.units' />}
                                value={formData.MileageMax}
                                type='text'
                                label={<TranslationText tKey='sandhills.mileageto' />}
                                class='wtb-mileage-to'
                                required={false}
                                validationErrors={validationErrors.MileageMax}
                                ref={(element) => inputReferences.current[`MileageMax`] = element}
                                onFieldChanged={textChange}
                            />
                            <div className='wtb-currency'>
                                {props.SelectListItems.MileageUnitOptions && props.SelectListItems.MileageUnitOptions.length > 0 &&
                                    <SelectField
                                        id={<TranslationText tKey='sandhills.tradepubwebsite.mvc.units' />}
                                        name='mileageUnit'
                                        value={mileageUnit}
                                        options={props.SelectListItems.MileageUnitOptions}
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.units' />}
                                        required={false}
                                        onFieldChanged={(e) => setMileageUnit(e.target.value)}
                                        disabled={props.SelectListItems.MileageUnitOptions.length == 1}
                                    />}
                            </div>
                        </div>
                    </span>}
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.horsepower.includes(o)) && formData.EquipmentType == '4' &&
                    <span>
                        <div className='wtb-input-half'>
                            <TextField
                                name='HorsepowerMin'
                                value={formData.HorsepowerMin}
                                type='text'
                                label={<TranslationText tKey='sandhills.horsepowerfrom' />}
                                required={false}
                                validationErrors={validationErrors.HorsepowerMin}
                                ref={(element) => inputReferences.current[`HorsepowerMin`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                        <div className='wtb-input-half '>
                            <TextField
                                name='HorsepowerMax'
                                value={formData.HorsepowerMax}
                                type='text'
                                label={<TranslationText tKey='sandhills.horsepowerto' />}
                                class='wtb-mileage-to'
                                required={false}
                                validationErrors={validationErrors.HorsepowerMax}
                                ref={(element) => inputReferences.current[`HorsepowerMax`] = element}
                                onFieldChanged={textChange}
                            />
                            <div className='wtb-currency'>
                                {props.SelectListItems.HorsepowerUnitOptions && props.SelectListItems.HorsepowerUnitOptions.length > 0 &&
                                    <SelectField
                                        name='horsepowerUnit'
                                        value={horsepowerUnit}
                                        options={props.SelectListItems.HorsepowerUnitOptions}
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.units' />}
                                        required={false}
                                        onFieldChanged={(e) => setHorsepowerUnit(e.target.value)}
                                        ref={(element) => inputReferences.current[`horsepowerUnit`] = element}
                                        disabled={props.SelectListItems.HorsepowerUnitOptions.length == 1}
                                    />}
                            </div>
                        </div>
                    </span>}
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.length.includes(o)) && formData.EquipmentType == '4' && formData.AlertTypeID === 1 &&
                    <span>
                        <div className='wtb-input-half wtb-combine-inputs'>
                            <TextField
                                name='LengthInFeet'
                                value={formData.LengthInFeet}
                                type='text'
                                label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.length' />}
                                required={false}
                                validationErrors={validationErrors.LengthInFeet}
                                ref={(element) => inputReferences.current[`LengthInFeet`] = element}
                                onFieldChanged={textChange}
                                placeholder='ft'
                            />
                            <TextField
                                name='LengthInInches'
                                value={formData.LengthInInches}
                                type='text'
                                label={<TranslationText tKey='sandhills.tradesites.mvc.lengthinches' />}
                                required={false}
                                validationErrors={validationErrors.LengthInInches}
                                ref={(element) => inputReferences.current[`LengthInInches`] = element}
                                onFieldChanged={textChange}
                                placeholder='in'
                            />
                        </div>
                    </span>}
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.width.includes(o)) && formData.EquipmentType == '4' && formData.AlertTypeID === 1 &&
                    <span>
                        <div className='wtb-input-half '>
                            <TextField
                                name='WidthInInches'
                                value={formData.WidthInInches}
                                type='text'
                                label={<TranslationText tKey='sandhills.crm.dscrm.widthinches' />}
                                required={false}
                                validationErrors={validationErrors.WidthInInches}
                                ref={(element) => inputReferences.current[`WidthInInches`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                    </span>}
                {availableSpecs.some((o) => listingAlertSpecNamesToCheck.engine.includes(o)) && formData.EquipmentType == '4' &&
                    <span>
                        <div className='wtb-input-half '>
                            <TextField
                                name='Engine'
                                value={formData.Engine}
                                type='text'
                                label={<TranslationText tKey='sandhills.enginemanufacturer' />}
                                required={false}
                                validationErrors={validationErrors.Engine}
                                ref={(element) => inputReferences.current[`Engine`] = element}
                                onFieldChanged={textChange}
                            />
                        </div>
                        <div className='wtb-input-half' />
                    </span>}
                {formData.EquipmentType == '3' &&
                    <TextField
                        name='SerialNumber'
                        value={formData.SerialNumber}
                        type='text'
                        label={<TranslationText tKey='sandhills.serialnum' />}
                        class='wtb-input-half '
                        required={false}
                        validationErrors={validationErrors.SerialNumber}
                        ref={(element) => inputReferences.current[`SerialNumber`] = element}
                        onFieldChanged={textChange}
                    />}
                {formData.EquipmentType == '9' &&
                    <TextField
                        name='PartNumber'
                        value={formData.PartNumber}
                        type='text'
                        label={<TranslationText tKey='sandhills.partnum' />}
                        class='wtb-input-half '
                        required={false}
                        validationErrors={validationErrors.PartNumber}
                        ref={(element) => inputReferences.current[`PartNumber`] = element}
                        onFieldChanged={textChange}
                    />}
                {formData.EquipmentType != '4' &&
                    <span className='wtb-subtitle m-top-20'><TranslationText tKey={'sandhills.tradepubwebsite.mvc.additionalinformation'} />:</span>}
                {formData.AlertTypeID === 2 && props.AllowDistanceLimitation &&
                    <>
                        <div className='wtb-input-half'>
                            <SelectField
                                id={'distance'}
                                name='mileageUnit'
                                value={formData.MaxDistance}
                                options={props.SelectListItems.DistanceLimitationOptions}
                                label={'Distance From Location (miles)'}
                                required={false}
                                onFieldChanged={(e) => setFormData(previousState => {
                                    return { ...previousState, MaxDistance: e.target.value };
                                })}
                            />
                        </div>
                        <div className='wtb-input-half'>
                            <TextField
                                name='sort-location-autosuggest'
                                value={userEnteredLocation}
                                class='wtb-location-search'
                                type='search'
                                label={'Location'}
                                id='sort-location'
                                required={false}
                                updateField={setUserEnteredLocation}
                                onFieldChanged={handleLocationFieldChange}
                                focus={false}
                                placeholder={translator.translate('sandhills.citystatezip')}
                                autoComplete={{
                                    ajaxUrl: (e) => getAutoCompleteLocationUrl(e),
                                    callback: getAutoCompleteCallback()
                                }}
                                autoCompleteDelay='1'
                                autoSuggestWidth={330}
                                onKeyPress={onKeyPress}
                                onFieldBlur={onLocationFieldBlur}
                                ref={(element) => inputReferences.current[`LocationField`] = element}
                            />
                            <button className='current-loc-btn ts-button' onClick={enterUsersCurrentLocation}>
                                <i className='fas fa-crosshairs' aria-hidden='true'></i>
                            </button>
                            {locationErrorMessage &&
                                <div className='error'>{locationErrorMessage}</div>
                            }
                        </div>
                    </>
                }
                <div className='wtb-input-half'>
                    <TextField
                        name='PriceLimit'
                        value={UnformatCurrency(formData.PriceLimit, formData.CultureCode, formData.CurrencyCode)}
                        type='text'
                        label={<TranslationText tKey='sandhills.pricelimit' />}
                        class='wtb-price'
                        validationErrors={validationErrors.PriceLimit}
                        ref={(element) => inputReferences.current[`PriceLimit`] = element}
                        onFieldChanged={textChange}
                        onBlurHandler={(e) => {
                            let value = e.target.value;
                            if (value) {
                                let unformattedCurrency = UnformatCurrency(value, formData.CultureCode, formData.CurrencyCode);
                                setFormData(previousState => {
                                    return {
                                        ...previousState,
                                        PriceLimit: UnformatCurrency(unformattedCurrency, formData.CultureCode, formData.CurrencyCode)
                                    };
                                });
                            }
                        }}
                    />
                    <div className='wtb-currency'>
                        <label htmlFor={translator.translate('sandhills.tradepubwebsite.mvc.currency')} className='bold'> <TranslationText tKey='sandhills.tradepubwebsite.mvc.currency' /> </label>
                        <select id={translator.translate('sandhills.tradepubwebsite.mvc.currency')} onChange={handleCurrencyChange} name='CurrencyCode' value={formData.CurrencyCode + ' ' + formData.CultureCode}>
                            {props.SelectListItems.CurrencyCodeOptions.map((item, index) =>
                                <option key={index} value={item.Text + ' ' + item.Value}>{item.Text}</option>)}
                        </select>
                        <span className='red'>{validationErrors.CurrencyCode}</span>
                    </div>
                </div>
                {formData.AlertTypeID == 2 &&
                    <div className='wtb-input-half'>
                        <input type='checkbox' id='add-watchlist-checkbox' checked={formData.AddToWatchlist} onChange={(e) => {
                            setFormData(previousState => {
                                return {
                                    ...previousState,
                                    AddToWatchlist: !formData.AddToWatchlist
                                }
                            });
                        }}
                        />
                        <label><TranslationText tKey='sandhills.tradesites.mvc.addmatchestomywatchlist' /></label>
                    </div>}
            </>
        );
    };

    const classificationBasedFields = () => {
        return (
            <>
                <SelectField
                    id='TopClassificationCategoryID'
                    name='TopClassificationCategoryID'
                    value={formData.TopClassificationCategoryID}
                    options={props.SelectListItems.TopClassificationCategoryOptions[formData.TopClassificationID]}
                    label={selectedTopClassificationLabel}
                    class='wtb-input-half'
                    required={true}
                    validationErrors={validationErrors.TopClassificationCategoryID}
                    ref={(element) => inputReferences.current[`TopClassificationCategoryID`] = element}
                    onFieldChanged={(e) => {
                        textChange(e);
                        setValidationErrors(previousState => {
                            return { ...previousState, CategoryID: '' };
                        });
                    }}
                />
                {formData.TopClassificationCategoryID && !topLevelClassificationsToSkipLowerClassifications.includes(Number(formData.TopClassificationCategoryID)) &&
                    <SelectField
                        id='CategoryID'
                        name='CategoryID'
                        value={formData.CategoryID}
                        options={props.SelectListItems.LowerClassificationCategoryOptions[formData.TopClassificationCategoryID]}
                        label={props.SelectListItems.LowerClassificationLabelMappings[formData.TopClassificationCategoryID]}
                        class='wtb-input-half '
                        required={true}
                        validationErrors={validationErrors.CategoryID}
                        ref={(element) => inputReferences.current[`CategoryID`] = element}
                        onFieldChanged={textChange}
                    />}

                {formData.AlertTypeID === 2 && props.AllowDistanceLimitation &&
                    <>
                        {!formData.TopClassificationCategoryID &&
                            <br />
                        }
                        <div className='wtb-input-half'>
                            <SelectField
                                id={'distance'}
                                name='mileageUnit'
                                value={formData.MaxDistance}
                                options={props.SelectListItems.DistanceLimitationOptions}
                                label={'Distance From Location (miles)'}
                                required={false}
                                onFieldChanged={(e) => setFormData(previousState => {
                                    return { ...previousState, MaxDistance: e.target.value };
                                })}
                            />
                        </div>
                        <div className='wtb-input-half'>
                            <TextField
                                name='sort-location-autosuggest'
                                value={userEnteredLocation}
                                class='wtb-location-search'
                                type='search'
                                label={'Location'}
                                id='sort-location'
                                required={false}
                                updateField={setUserEnteredLocation}
                                onFieldChanged={handleLocationFieldChange}
                                focus={false}
                                placeholder={translator.translate('sandhills.citystatezip')}
                                autoComplete={{
                                    ajaxUrl: (e) => getAutoCompleteLocationUrl(e),
                                    callback: getAutoCompleteCallback()
                                }}
                                autoCompleteDelay='1'
                                autoSuggestWidth={330}
                                onKeyPress={onKeyPress}
                                onFieldBlur={onLocationFieldBlur}
                                ref={(element) => inputReferences.current[`LocationField`] = element}
                            />
                            <button className='current-loc-btn ts-button' onClick={enterUsersCurrentLocation}>
                                <i className='fas fa-crosshairs' aria-hidden='true'></i>
                            </button>
                            {locationErrorMessage &&
                                <div className='error'>{locationErrorMessage}</div>
                            }
                        </div>
                    </>
                }
            </>
        );
    };

    return (
        <div>
            {currentUser?.IsAuthenticated ?
                <div className={'want-to-buy-container' + (formData.AlertTypeID === 2 ? ' email-alerts-container' : '')}>
                    {!props.isChild && <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<TranslationText tKey='sandhills.tradepubwebsite.postawanttobuy' />} />}
                    <div className=' pad-tb font-14'>
                        {header(formData.AlertTypeID)}
                        {props.AlertTypeID == 1 &&
                            <form id='wtb-form' method='post' onSubmit={submitForm}>
                                <ValidationErrors errors={validationErrors} />
                                <div className='want-to-buy-form'>
                                    <input type='hidden' value={formData.WantToBuyMessageID} name='WantToBuyMessageID' />
                                    {formHeader(formData.AlertTypeID)}
                                    {siteHasClassifications &&
                                        <>
                                            <span className='wtb-subtitle'>{props.SubTitle}</span>
                                            <div className='capitalize'>
                                                <fieldset className='ts__fieldset'>
                                                    <legend className='ts__legend visually-hidden'><strong>{translator.translate('sandhills.crm.dscrm.lookingfor')}</strong></legend>
                                                    <RadioButtonList
                                                        options={props.SelectListItems.TopClassificationOptions}
                                                        class='wtb-input-inline-radios'
                                                        name='TopClassificationID'
                                                        value={formData.TopClassificationID}
                                                        validationErrors={validationErrors.TopClassificationID}
                                                        label={<TranslationText tKey='sandhills.crm.dscrm.lookingfor' />}
                                                        required={true}
                                                        onFieldChanged={(e) => {
                                                            let value = e.target.value;
                                                            setValidationErrors({});
                                                            setSelectedTopClassificationLabel(getTopSelectedClassificationLabel(parseInt(value)));
                                                            setFormData(previousState => {
                                                                return {
                                                                    ...previousState,
                                                                    TopClassificationID: value,
                                                                    TopClassificationCategoryID: ''
                                                                }
                                                            });
                                                        }}
                                                    />
                                                </fieldset>
                                            </div>
                                        </>}
                                    <div className='wtb-btns'>
                                        {!showEquipmentInputDetails && formData.AlertTypeID === 1 &&
                                            <>
                                                <button className='continue-btn' onClick={() => setShowEquipmentInputDetails(showEquipmentInputDetails => !showEquipmentInputDetails)}>
                                                    <TranslationText tKey='sandhills.continue' />
                                                </button>
                                                <button className='cancel-btn' onClick={handleCancelClick}>
                                                    <TranslationText tKey='sandhills.cancel' />
                                                </button>
                                            </>}
                                    </div>
                                    <div className={'wtb-input-details ' + (showEquipmentInputDetails || formData.AlertTypeID === 2 ? '' : 'hide')}>
                                        {!siteHasClassifications &&
                                            <>
                                                <p><TranslationText tKey='sandhills.pleasebesuretofillinmanumodelcatfieldsforemails' /></p>
                                                <span className='wtb-subtitle'>{props.SubTitle}</span>
                                            </>}
                                        <div className='wtb-input'>
                                            {siteHasClassifications ? classificationBasedFields() : equipmentBasedFields()}
                                            {formData.AlertTypeID === 1 ? wtbFields() : listingAlertFields()}
                                        </div>
                                        {formData.showAlertNumberExceededMessage && !formData.WantToBuyMessageID > 0 && formData.AlertTypeID === 2 &&
                                            <div className="alert-error-message">
                                                <br />
                                                <div className="error">
                                                    {translator.translate("sandhills.tradesites.mvc.alertnumberexceeded", [alertTypeName, alertTypeName, alertTypeName])}
                                                    {props.AlertTypeID === 1 && <a href="/my-want-to-buys" style={{ color: "black" }}> <TranslationText tKey="sandhills.viewmywtbs" /></a>}
                                                </div>
                                            </div>}
                                        <div className='wtb-btns'>
                                            <button type='submit' id='btnSave' className={'ts-button ' + (isSaveDisabled || (formData.showAlertNumberExceededMessage && !formData.WantToBuyMessageID > 0) ? 'disabled-btn' : '') + ' continue-btn'} disabled={(isSaveDisabled ? 'disabled' : '')} >
                                                <TranslationText tKey='SandhillsSystem.Save' />
                                            </button>
                                            <button className='cancel-btn' onClick={handleCancelClick}>
                                                <TranslationText tKey='sandhills.cancel' />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        }
                        <div>
                            {formData.AlertTypeID === 2 &&
                                <div>
                                    <EmailAlertsListView
                                        EmailAlerts={emailAlerts}
                                        availableSpecs={availableSpecs}
                                        MileageUnit={mileageUnit}
                                        HorsepowerUnit={horsepowerUnit}
                                        handleAddClick={handleAddClick}
                                        deleteEmailAlert={deleteEmailAlert}
                                        HasClassifications={siteHasClassifications}
                                        AllowDistanceLimitation={props.AllowDistanceLimitation}
                                        toggleWatchlist={toggleWatchlist}
                                        formHeader={formHeader}
                                        SelectListItems={props.SelectListItems}
                                        userEnteredLocation={userEnteredLocation}
                                        handleLocationFieldChange={handleLocationFieldChange}
                                        handleCurrencyChange={handleCurrencyChange}
                                        validationErrors={validationErrors}
                                        textChange={textChange}
                                        getAutoCompleteCallback={getAutoCompleteCallback}
                                        getAutoCompleteLocationUrl={getAutoCompleteLocationUrl}
                                        enterUsersCurrentLocation={enterUsersCurrentLocation}
                                        locationErrorMessage={locationErrorMessage}
                                        formData={formData}
                                        setFormData={setFormData}
                                        submitForm={submitForm}
                                        getAutoCompleteManuData={getAutoCompleteManuData}
                                        getAutoCompleteModelData={getAutoCompleteModelData}
                                        getAutoCompleteUrl={getAutoCompleteUrl}
                                        fieldChange={fieldChange}
                                        runValidation={runValidation}
                                        showAlertNumberExceededMessage={formData.showAlertNumberExceededMessage}
                                        titleSort={titleSort}
                                        categoryContextOverride={props.categoryContextOverride}
                                        setEmailAlerts={setEmailAlerts}
                                        ModelNotRequiredCats={props.ModelNotRequiredCats}
                                        toggleTextAlert={toggleTextAlert}
                                        TextAlertFeatureEnabled={props.TextAlertFeatureEnabled}
                                        {...props}
                                    />
                                    {showSuccessModal &&
                                        <EmailAlertsChangesSavedModal
                                            onClose={closeSuccessModal}
                                            message={translator.translate('sandhills.tradesites.mvc.changeshavebeensaved')}
                                        />
                                    }
                                </div>}
                        </div>
                    </div>
                </div>
                :
                <div className='wtb-splash'>
                    <div className='main'>
                        <div className='container'>
                            <div className='breadcrumbs'>
                                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<TranslationText tKey='sandhills.tradepubwebsite.mvc.postafreewanttobuy' />} />
                            </div>
                            <div className='wrapper'>
                                <div className='flex-col'>
                                    <h1>{translator.translate('sandhills.tradesites.mvc.letustaketheworkoutofsearching').replace("{0}", props.SplashPageH1Replacement)}</h1>
                                    <p>{translator.translate('sandhills.tradesites.mvc.wanttobuyeffortless').replaceAll("{0}", props.FormalDisplayName).replace("{1}", props.SplashPageFindReplacement).replace("{2}", props.SplashPageListingsReplacement).replace("  ", " ")}</p>
                                    <p className="primary"><strong>{translator.translate('sandhills.tradesites.mvc.readytogiveitatry')}</strong></p>
                                    <a className='block-btn' href={"/sso/login"} target='_self'>{translator.translate('sandhills.tradesites.mvc.signintogetstarted')}</a> 
                                </div>
                                <div className='flex-col'>
                                    <img src='/Content/images/wtb-form.jpg' alt={translator.translate('sandhills.tradesites.mvc.wanttobuyimagealttext')}></img>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='main grey-section'>
                        <div className='container'>
                            <div className='flex-row'>
                                <div className='flex-col'>
                                    <h2>{translator.translate('sandhills.tradesites.mvc.wanttobuygamechanger')}</h2>
                                    <div className='icon-row block'>
                                        <ul>
                                            <li>
                                                <i className="fa-regular fa-paper-plane"></i>
                                                <p><strong>{"1. " + translator.translate('sandhills.tradesites.mvc.submitwanttobuyrequest')}</strong> {translator.translate('sandhills.tradesites.mvc.sharewhatlookingfor' + (currentSite.Site.IsLivestockMarket || currentSite.Site.IsEquineMarket ? 'nonequipment' : ''))}</p>
                                            </li>
                                            <li>
                                                <i className="fa-regular fa-envelope"></i>
                                                <p><strong>{"2. " + translator.translate('sandhills.tradesites.mvc.keepaneyeout')}</strong> {translator.translate('sandhills.tradesites.mvc.wanttobuyprocess')}</p>
                                            </li>
                                            <li>
                                                <i className="fa-regular fa-comments"></i>
                                                <p><strong>{"3. " + translator.translate('sandhills.tradesites.mvc.instantalertsforsellers')}</strong> {translator.translate('sandhills.tradesites.mvc.sellersgetnotified')}</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div className='flex-col'>
                                    <h2>{translator.translate('sandhills.tradesites.mvc.wanttobuymustusetool')}</h2>
                                    <div className='icon-row block'>
                                        <ul>
                                            <li>
                                                <i className="fa-regular fa-bell"></i>
                                                <p><strong>{translator.translate('sandhills.tradesites.mvc.getinstantresults')}</strong> {translator.translate('sandhills.tradesites.mvc.nowaitingnoguesswork')}</p>
                                            </li>
                                            <li>
                                                <i className="fa-regular fa-clock"></i>
                                                <p><strong>{translator.translate('sandhills.tradesites.mvc.savetimeandeffort')}</strong> {translator.translate('sandhills.tradesites.mvc.handlethesearchforyou').replace("{0}", props.FormalDisplayName)}</p>
                                            </li>
                                            <li>
                                                <i className="fa-regular fa-money-bill-1"></i>
                                                <p><strong>{translator.translate('sandhills.tradesites.mvc.itsfree')}</strong> {translator.translate('sandhills.tradesites.mvc.smartershopping')}</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='wtb-banner'>
                        <h2><strong>{translator.translate('sandhills.tradesites.mvc.readytogiveitatry').split("?")[0] + "?"}</strong> {translator.translate('sandhills.tradesites.mvc.readytogiveitatry').split("?")[1]?.trim()}</h2>
                        <a className='block-btn' href={"/sso/login"} target='_self'>{translator.translate('sandhills.tradesites.mvc.signintogetstarted')}</a>
                    </div>
                </div>}
        </div>
    );
};

export default WantToBuyView;
global.WantToBuyView = WantToBuyView;